import { type SupportedLocale } from './commandRegistry'

export interface VoiceCommandHelp {
  phrase: string
  description: string
}

export interface VoiceStrings {
  enableTitle: string
  confirmTitle: string
  confirmDescription: string
  confirmEnable: string
  confirmCancel: string
  listening: string
  helpTitle: string
  helpIntro: string
  helpClose: string
  helpDisable: string
  commands: VoiceCommandHelp[]
}

const VOICE_STRINGS: Record<SupportedLocale, VoiceStrings> = {
  'pt-BR': {
    enableTitle: 'Ativar controle por voz',
    confirmTitle: 'Ativar controle por voz?',
    confirmDescription:
      'Ao ligar, o controle por voz fica habilitado até você desligar novamente. Você poderá comandar a narração falando.',
    confirmEnable: 'Ligar',
    confirmCancel: 'Cancelar',
    listening: 'Ouvindo',
    helpTitle: 'Comandos de voz',
    helpIntro: 'Com o controle por voz ligado, fale um destes comandos:',
    helpClose: 'Entendi',
    helpDisable: 'Desligar controle por voz',
    commands: [
      {
        phrase: '“iniciar” / “continuar”',
        description: 'Inicia ou retoma a narração'
      },
      { phrase: '“pausar”', description: 'Pausa a narração' },
      { phrase: '“parar”', description: 'Para a narração' },
      { phrase: '“avançar”', description: 'Avança 10 segundos' },
      { phrase: '“voltar”', description: 'Volta 10 segundos' },
      {
        phrase: '“próximo capítulo”',
        description: 'Vai para o próximo capítulo'
      },
      {
        phrase: '“capítulo anterior”',
        description: 'Volta para o capítulo anterior'
      },
      { phrase: '“mais rápido”', description: 'Aumenta a velocidade' },
      { phrase: '“mais devagar”', description: 'Diminui a velocidade' }
    ]
  },
  'en-US': {
    enableTitle: 'Turn on voice control',
    confirmTitle: 'Turn on voice control?',
    confirmDescription:
      'Once on, voice control stays enabled until you turn it off again. You can control the narration by speaking.',
    confirmEnable: 'Turn on',
    confirmCancel: 'Cancel',
    listening: 'Listening',
    helpTitle: 'Voice commands',
    helpIntro: 'With voice control on, say one of these commands:',
    helpClose: 'Got it',
    helpDisable: 'Turn off voice control',
    commands: [
      {
        phrase: '“play” / “continue”',
        description: 'Start or resume narration'
      },
      { phrase: '“pause”', description: 'Pause narration' },
      { phrase: '“stop”', description: 'Stop narration' },
      { phrase: '“forward”', description: 'Skip forward 10 seconds' },
      { phrase: '“back”', description: 'Skip back 10 seconds' },
      { phrase: '“next chapter”', description: 'Go to the next chapter' },
      {
        phrase: '“previous chapter”',
        description: 'Go to the previous chapter'
      },
      { phrase: '“faster”', description: 'Increase speed' },
      { phrase: '“slower”', description: 'Decrease speed' }
    ]
  },
  'es-419': {
    enableTitle: 'Activar control por voz',
    confirmTitle: '¿Activar el control por voz?',
    confirmDescription:
      'Al activarlo, el control por voz queda habilitado hasta que lo desactives de nuevo. Podrás controlar la narración hablando.',
    confirmEnable: 'Activar',
    confirmCancel: 'Cancelar',
    listening: 'Escuchando',
    helpTitle: 'Comandos de voz',
    helpIntro: 'Con el control por voz activado, di uno de estos comandos:',
    helpClose: 'Entendido',
    helpDisable: 'Desactivar control por voz',
    commands: [
      {
        phrase: '“iniciar” / “continuar”',
        description: 'Inicia o reanuda la narración'
      },
      { phrase: '“pausar”', description: 'Pausa la narración' },
      { phrase: '“parar”', description: 'Detiene la narración' },
      { phrase: '“avanzar”', description: 'Avanza 10 segundos' },
      { phrase: '“retroceder”', description: 'Retrocede 10 segundos' },
      {
        phrase: '“siguiente capítulo”',
        description: 'Va al siguiente capítulo'
      },
      {
        phrase: '“capítulo anterior”',
        description: 'Vuelve al capítulo anterior'
      },
      { phrase: '“más rápido”', description: 'Aumenta la velocidad' },
      { phrase: '“más lento”', description: 'Reduce la velocidad' }
    ]
  }
}

export function getVoiceStrings(locale: SupportedLocale): VoiceStrings {
  return VOICE_STRINGS[locale] ?? VOICE_STRINGS['pt-BR']
}
